/* Importing stylesheets */
import "./brioche.css";
import {
  BRIOCHE_DEFAULT_ARGS,
  BRIOCHE_PART_FORMATTING_CLASSES_MAP,
  BRIOCHE_POSITION_CLASSES_MAP,
  BRIOCHE_ROOT_SELECTOR,
  BRIOCHE_WRAPPER_SELECTOR
} from "./Brioche.constant";
import { createRoot, Root } from "react-dom/client";
import { Brioche } from "./Brioche";
import { BriocheDisplay } from "./BriocheDisplay";
import { BriocheArgs } from "./BriocheArgs";
import { BriocheSettings } from "./BriocheSettings";

/**
 * Brioche is basically a custom toast.
 * It should display short messages with optional icons and controls.
 *
 * This is the controlling class for all Brioche components in the app.
 * The messages are queued and get shown after each other.
 */
class BriocheEngine implements BriocheDisplay {
  private readonly key = `${Date.now().toString(10)}_${Math.floor(Math.random() * 10000).toString(10)}`;
  private readonly queue: BriocheSettings[] = [];
  private isRendering: boolean = false;
  private readonly root: Root;

  private readonly rootElem: HTMLDivElement;
  private wrapperElement?: Element;

  public constructor() {
    this.rootElem = document.createElement("div");
    this.rootElem.setAttribute("id", this.key);
    this.rootElem.setAttribute(BRIOCHE_ROOT_SELECTOR, "");

    this.root = createRoot(this.rootElem);
  }

  /**
   * Selects the actually preferred wrapper element and append the Brioche root.
   * This might come handy then certain elements are wrapped in something, but
   * Brioche should slide behind them.
   * @private
   */
  private placeRootIntoWrapper() {
    const currentWrapper =
      document.body.querySelector(BRIOCHE_WRAPPER_SELECTOR) ?? document.body;
    if (this.wrapperElement && this.wrapperElement !== currentWrapper) {
      this.wrapperElement.removeChild(this.rootElem);
      this.wrapperElement = undefined;
    }
    if (!this.wrapperElement) {
      this.wrapperElement = currentWrapper;
      this.wrapperElement.appendChild(this.rootElem);
    }
  }

  private addToQueue(settings: BriocheSettings) {
    this.queue.push(settings);
    this.render();
  }

  private argsToSettings(args: BriocheArgs): BriocheSettings {
    return {
      ...BRIOCHE_DEFAULT_ARGS,
      ...BRIOCHE_POSITION_CLASSES_MAP[
        args.position ?? BRIOCHE_DEFAULT_ARGS.position
      ],
      ...BRIOCHE_PART_FORMATTING_CLASSES_MAP[
        args.theme ?? BRIOCHE_DEFAULT_ARGS.theme
      ],
      ...args
    };
  }

  /**
   * This function initiates the animation on deactivation
   * @param settings
   * @private
   */
  private createDeactivatorFn(settings: BriocheSettings) {
    return () => {
      const actSettings: BriocheSettings = {
        ...settings,
        classPositionActive: ""
      };
      this.root.render(<Brioche key={this.key} settings={actSettings} />);
      setTimeout(
        this.createFinalizerFn(actSettings),
        actSettings.animationTimeout
      );
    };
  }

  /**
   * This function removes the Brioche from the DOM at the end of the deactivation animation
   * @param settings
   * @private
   */
  private createFinalizerFn(settings: BriocheSettings) {
    return () => {
      const actSettings: BriocheSettings = {
        ...settings,
        classVisible: ""
      };
      this.root.render(<Brioche key={this.key} settings={actSettings} />);
      this.isRendering = false;
      this.render();
    };
  }

  /**
   * Sets some CSS variables dynamically, so the Brioche messages could follow changes in the page.
   * E.g.: Member navigates to Chat tab
   * @param settings
   */
  private readonly setCssVars = (settings: BriocheSettings) => {
    // Set the position of the wrapper
    this.rootElem.classList.remove(...this.rootElem.classList.values());
    this.rootElem.classList.add(settings.classPosition);

    // Set root variables
    this.rootElem.style.setProperty(
      "--animation-timeout",
      `${settings.animationTimeout}ms`
    );
    this.rootElem.style.setProperty(
      "--z-index-value",
      settings.zIndex.toString()
    );
  };

  /**
   * Shows the next Brioche message (if exists), and registers the cleanup functions
   */
  private readonly render = () => {
    if (this.isRendering) {
      return;
    }
    const settings = this.queue.shift();
    if (!settings) {
      return;
    }
    this.placeRootIntoWrapper();
    this.isRendering = true;
    const preRenderSettings: BriocheSettings = {
      ...settings,
      classPositionActive: ""
    };
    this.setCssVars(settings);
    // Adding Brioche to the DOM
    this.root.render(<Brioche key={this.key} settings={preRenderSettings} />);
    requestAnimationFrame(() =>
      setTimeout(() => {
        // Starting animation and registering cleanup
        this.root.render(<Brioche key={this.key} settings={settings} />);
        setTimeout(this.createDeactivatorFn(settings), settings.timeout);
      })
    );
  };

  /**
   * Puts a message to the end of the queue.
   * @param args
   */
  public show(args: BriocheArgs): void {
    // Let's prevent memory leaks
    if (this.queue.length > 100) {
      // Some error handling would be nice here. Let's wait while we exceed
      // the startup stage and could allocate enough time to do things properly...
      this.queue.shift();
    }
    this.addToQueue(this.argsToSettings(args));
  }

  /**
   * Themed alias of `show()`
   * @param args
   */
  public success(args: Omit<BriocheArgs, "theme">) {
    this.show({ ...args, theme: "success" });
  }

  /**
   * Themed alias of `show()`
   * @param args
   */
  public error(args: Omit<BriocheArgs, "theme">) {
    this.show({ ...args, theme: "error" });
  }
}

// Singleton instance
export const brioche = new BriocheEngine();
